import { useState, useEffect, useRef } from "react";
import { useAuth, useCart, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Trash2, Minus, Plus, Printer, ChevronDown } from "lucide-react";
import ReceiptPreview from "../components/ReceiptPreview";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_pos-register-pro/artifacts/r59p7j84_PHOTO-2025-11-25-13-33-44.jpg";

const paymentMethods = [
  { id: "Cash", label: "CASH", color: "bg-blue-500" },
  { id: "Credit", label: "CREDIT", color: "bg-blue-500" },
  { id: "Debit", label: "DEBIT", color: "bg-blue-500" },
  { id: "Gift", label: "GIFT", color: "bg-orange-500" },
  { id: "Online", label: "ONLINE", color: "bg-teal-500" },
  { id: "Bank", label: "BANK", color: "bg-blue-500" },
];

export default function SalesReceipt() {
  const { getAuthHeader } = useAuth();
  const { cartItems, addToCart, updateQuantity, removeFromCart, clearCart, subtotal, tax, total, discount, setDiscount } = useCart();
  
  const [products, setProducts] = useState([]);
  const [selectedProduct, setSelectedProduct] = useState("");
  const [barcodeInput, setBarcodeInput] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [amountPaid, setAmountPaid] = useState("");
  const [changeDue, setChangeDue] = useState(0);
  
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [completedSale, setCompletedSale] = useState(null);
  
  const barcodeRef = useRef(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`, getAuthHeader());
      setProducts(response.data);
    } catch (error) {
      console.error("Failed to fetch products:", error);
    }
  };

  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      addToCart(product);
      setSelectedProduct("");
    }
  };

  const handleBarcodeSubmit = async (e) => {
    e.preventDefault();
    if (!barcodeInput.trim()) return;
    
    try {
      const response = await axios.get(`${API}/products/barcode/${barcodeInput}`, getAuthHeader());
      addToCart(response.data);
      setBarcodeInput("");
    } catch (error) {
      toast.error("Product not found");
    }
    barcodeRef.current?.focus();
  };

  const handleBarcodeDropdown = async () => {
    // Search and add first matching product
    if (barcodeInput.trim()) {
      handleBarcodeSubmit({ preventDefault: () => {} });
    }
  };

  const handlePaymentSelect = (method) => {
    if (cartItems.length === 0) {
      toast.error("Please add items to cart first");
      return;
    }
    setSelectedPayment(method);
    setAmountPaid(total.toFixed(2));
    setChangeDue(0);
    setShowPaymentModal(true);
  };

  const handleAmountChange = (value) => {
    setAmountPaid(value);
    const paid = parseFloat(value) || 0;
    setChangeDue(Math.max(0, paid - total));
  };

  const handleCompleteSale = async (isHeld = false) => {
    if (cartItems.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    if (!isHeld && !selectedPayment) {
      toast.error("Please select a payment method");
      return;
    }

    try {
      const saleData = {
        items: cartItems,
        subtotal,
        tax,
        discount,
        total,
        payment_method: selectedPayment || "Cash",
        amount_paid: parseFloat(amountPaid) || total,
        change_due: changeDue,
        is_held: isHeld
      };

      const response = await axios.post(`${API}/sales`, saleData, getAuthHeader());
      
      if (isHeld) {
        toast.success("Sale put on hold");
      } else {
        setCompletedSale(response.data);
        setShowReceiptModal(true);
        toast.success("Sale completed!");
      }
      
      clearCart();
      setShowPaymentModal(false);
      setSelectedPayment(null);
      setAmountPaid("");
      setChangeDue(0);
    } catch (error) {
      toast.error("Failed to complete sale");
    }
  };

  const handlePrintReceipt = () => {
    window.print();
  };

  const handleCancel = () => {
    clearCart();
    setShowPaymentModal(false);
    setSelectedPayment(null);
    toast.info("Sale cancelled");
  };

  return (
    <div className="animate-fade-in" data-testid="sales-receipt-page">
      {/* Top Bar - Product Selection */}
      <div className="bg-white rounded-lg shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Product Dropdown */}
          <Select value={selectedProduct} onValueChange={handleProductSelect}>
            <SelectTrigger data-testid="product-select">
              <SelectValue placeholder="Select a product" />
            </SelectTrigger>
            <SelectContent>
              {products.map((product) => (
                <SelectItem key={product.id} value={product.id}>
                  {product.name} - ${product.price.toFixed(2)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Barcode Scanner Input with Orange Button */}
          <div className="flex">
            <Input
              ref={barcodeRef}
              value={barcodeInput}
              onChange={(e) => setBarcodeInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleBarcodeSubmit(e)}
              placeholder="Scan or enter item information"
              className="h-10 rounded-r-none border-r-0"
              data-testid="barcode-input"
            />
            <button
              onClick={handleBarcodeDropdown}
              className="h-10 px-3 bg-orange-500 hover:bg-orange-600 text-white rounded-r-md flex items-center justify-center"
              data-testid="barcode-dropdown-btn"
            >
              <ChevronDown className="w-5 h-5" />
            </button>
          </div>

          {/* Customer Search */}
          <Input
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
            placeholder="Select customer by name or phone number"
            className="h-10"
            data-testid="customer-search"
          />
        </div>
      </div>

      {/* Cart Table */}
      <div className="bg-white rounded-lg shadow-sm mb-4 overflow-hidden">
        <table className="cart-table" data-testid="cart-table">
          <thead>
            <tr>
              <th>Item #</th>
              <th>Item Name</th>
              <th>Attribute</th>
              <th>Size</th>
              <th>Qty</th>
              <th>Price</th>
              <th>Ext Price</th>
              <th>Tax (0%)</th>
              <th>Discount %</th>
              <th>Discount Amount</th>
              <th>Discount</th>
              <th>Delete</th>
            </tr>
          </thead>
          <tbody>
            {cartItems.length === 0 ? (
              <tr>
                <td colSpan="12" className="text-center py-8 text-slate-400">
                  No items in cart. Add products to begin.
                </td>
              </tr>
            ) : (
              cartItems.map((item, index) => {
                const extPrice = item.price * item.quantity;
                const itemTax = 0; // 0% tax
                const discountAmt = extPrice * (item.discount_percent / 100);
                return (
                  <tr key={item.product_id} className="animate-slide-in">
                    <td className="font-mono">{index + 1}</td>
                    <td className="font-medium">{item.name}</td>
                    <td className="text-slate-400">-</td>
                    <td className="text-slate-400">-</td>
                    <td>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          className="p-1 hover:bg-slate-100 rounded"
                          data-testid={`qty-minus-${item.product_id}`}
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span className="w-8 text-center font-mono">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          className="p-1 hover:bg-slate-100 rounded"
                          data-testid={`qty-plus-${item.product_id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                    <td className="font-mono">${item.price.toFixed(2)}</td>
                    <td className="font-mono font-medium">${extPrice.toFixed(2)}</td>
                    <td className="font-mono">${itemTax.toFixed(2)}</td>
                    <td>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        value={item.discount_percent}
                        onChange={(e) => {}}
                        className="w-16 h-8 text-center"
                        data-testid={`discount-${item.product_id}`}
                      />
                    </td>
                    <td className="font-mono text-red-500">-${discountAmt.toFixed(2)}</td>
                    <td className="font-mono text-green-600">-${discountAmt.toFixed(2)}</td>
                    <td>
                      <button
                        onClick={() => removeFromCart(item.product_id)}
                        className="p-2 text-red-500 hover:bg-red-50 rounded"
                        data-testid={`delete-${item.product_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
            {/* Totals */}
            <tr className="bg-slate-50 border-t-2">
              <td colSpan="6" className="text-right font-semibold">Sub Total</td>
              <td className="font-mono font-bold">${subtotal.toFixed(2)}</td>
              <td colSpan="5"></td>
            </tr>
            <tr className="bg-slate-50">
              <td colSpan="6" className="text-right font-semibold">Tax</td>
              <td className="font-mono">${tax.toFixed(2)}</td>
              <td colSpan="5"></td>
            </tr>
            <tr className="bg-slate-50">
              <td colSpan="6" className="text-right font-semibold">Discount</td>
              <td className="font-mono text-green-600">${discount.toFixed(2)}</td>
              <td colSpan="5"></td>
            </tr>
            <tr className="bg-blue-50 border-t-2">
              <td colSpan="6" className="text-right font-bold text-lg">Total</td>
              <td className="font-mono font-bold text-lg text-blue-600">${total.toFixed(2)}</td>
              <td colSpan="5"></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Payment Methods and Actions */}
      <div className="bg-white rounded-lg shadow-sm p-4">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div className="flex flex-wrap gap-3">
            {paymentMethods.map((method) => (
              <button
                key={method.id}
                onClick={() => handlePaymentSelect(method.id)}
                className={`pos-payment-btn ${method.color} text-white min-w-[100px] h-12 px-6 font-bold rounded-md hover:brightness-110 active:scale-95 transition-all`}
                data-testid={`payment-${method.id.toLowerCase()}`}
              >
                {method.label}
              </button>
            ))}
          </div>
          
          <div className="flex items-center gap-4">
            <span className="text-2xl font-bold text-slate-700">
              Amount Due: <span className="text-blue-600">${total.toFixed(2)}</span>
            </span>
          </div>
        </div>

        <div className="flex justify-end gap-3 mt-4 pt-4 border-t">
          <Button
            variant="secondary"
            onClick={() => {}}
            className="bg-slate-400 hover:bg-slate-500 text-white"
            data-testid="held-receipts-btn"
          >
            HELD RECEIPTS
          </Button>
          <Button
            variant="destructive"
            onClick={handleCancel}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="cancel-btn"
          >
            CANCEL
          </Button>
          <Button
            variant="outline"
            onClick={() => handleCompleteSale(true)}
            disabled={cartItems.length === 0}
            className="border-slate-300"
            data-testid="save-only-btn"
          >
            SAVE ONLY
          </Button>
          <Button
            onClick={() => {
              if (cartItems.length > 0 && selectedPayment) {
                handleCompleteSale(false);
              } else if (cartItems.length > 0) {
                setShowPaymentModal(true);
                setSelectedPayment("Cash");
                setAmountPaid(total.toFixed(2));
              }
            }}
            className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-300"
            disabled={cartItems.length === 0}
            data-testid="save-print-btn"
          >
            <Printer className="w-4 h-4 mr-2" />
            SAVE & PRINT
          </Button>
        </div>
      </div>

      {/* Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={setShowPaymentModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">
              Payment - {selectedPayment}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex justify-between items-center p-4 bg-slate-50 rounded-lg">
              <span className="font-semibold">Total Amount:</span>
              <span className="text-2xl font-bold text-blue-600">${total.toFixed(2)}</span>
            </div>
            
            {selectedPayment === "Cash" && (
              <>
                <div className="space-y-2">
                  <label className="font-medium text-slate-700">Amount Received:</label>
                  <Input
                    type="number"
                    step="0.01"
                    value={amountPaid}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    className="text-xl h-14 font-mono"
                    autoFocus
                    data-testid="amount-paid-input"
                  />
                </div>
                <div className="flex justify-between items-center p-4 bg-green-50 rounded-lg">
                  <span className="font-semibold">Change Due:</span>
                  <span className="text-2xl font-bold text-green-600">${changeDue.toFixed(2)}</span>
                </div>
              </>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setShowPaymentModal(false)}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={() => handleCompleteSale(false)}
                className="flex-1 bg-green-600 hover:bg-green-700"
                disabled={selectedPayment === "Cash" && parseFloat(amountPaid) < total}
                data-testid="complete-payment-btn"
              >
                Complete Sale
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Receipt Modal */}
      <Dialog open={showReceiptModal} onOpenChange={setShowReceiptModal}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex justify-between items-center">
              Receipt Preview
              <Button size="sm" onClick={handlePrintReceipt} data-testid="print-receipt-btn">
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
            </DialogTitle>
          </DialogHeader>
          {completedSale && (
            <ReceiptPreview sale={completedSale} logoUrl={LOGO_URL} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
